import { ProposalDAOInterface } from '../../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../../ProposalDAOImpl';
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';
import { ProposalFieldDAO } from '../../ProposalFieldDAO';
import { ComparisonItemDAO } from '../../ProposalItemDAO';
import { ProposalStyleClassNames } from '../../../ProposalStyleClassNames';
import { DataPointResponse, DataPoints, ESGCarbonIntensityDetails } from '../../DataPoints';
import DonutData from '@/types/donut-data';

/**
 * @class ESGCarbonIntensityDAO Data Access Object
 */
export default class ESGCarbonIntensityDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new ESGCarbonIntensityDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['ESG_CARBON_INTENSITY_COLORS'];
  }

  protected build(): void {
    const max = 5;

    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      const { response, disclaimerKeys }: DataPointResponse<ESGCarbonIntensityDetails> = DataPoints.getESGCarbonIntensityData(itemDAO.item);
      const label = this.formatMessage(`ESGComparisonTool.Carbon.Intensity.${response.key}`);
      this.addDisclaimerKeys(disclaimerKeys, itemDAO.item);

      if (response.transposedValue) {
        const carbonIntensityField = new ProposalFieldDAO(label, response.transposedValue);
        carbonIntensityField.key = response.key;
        this.addFieldDAO(itemDAO, carbonIntensityField);

        // Fill the chart

        for (let i = 0; i < max - response.transposedValue; i++) {
          const naField = new ProposalFieldDAO(this.formatMessage('ESGComparisonTool.Carbon.Intensity.NA'), 1);
          naField.key = 'NA';
          this.addFieldDAO(itemDAO, naField);
        }
      } else {
        const naField = new ProposalFieldDAO(this.formatMessage('ESGComparisonTool.Carbon.Intensity.NA'), 5);
        naField.key = 'NA';
        this.addFieldDAO(itemDAO, naField);
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    const { configOptions } = this.proposalElement.dataView as ProposalNS.DonutViewOptions;

    if (out && configOptions.title) {
      const item: ComparisonItemDAO = this.getItemDAOs()[index];
      const { response }: DataPointResponse<ESGCarbonIntensityDetails> = DataPoints.getESGCarbonIntensityData(item.item);
      const label = this.formatMessage(`ESGComparisonTool.Carbon.Intensity.${response.key}`);

      out.title = {
        ...configOptions.title,
        text: configOptions.title.text
          .replace(
            '{{value}}',
            response.value
              ? `<span class="value">${response.value.toFixed(1)}</span> <span class="unit">t</span>`
              : `<span class="na">${this.formatMessage('Common.NotApplicable')}</span>`,
          )
          .replace('{{label}}', label)
          .replace('{{color}}', this.colorsDict ? this.colorsDict[response.key] : ''),
      };
    }

    return out;
  }

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: any, celltype: any): number {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = celltype;
    this.addFieldDAO(itemDAO, field);
    return value;
  }
}
